import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ErrorAlert from 'common/components/alert/ErrorAlert';
import Modal from 'common/components/modal/Modal';
import Page from 'common/components/page/Page';
import { PageTemplates } from 'common/components/page/PageTemplates';
import ActionStatus from 'common/types/ActionStatus';
import ApplicationOverview from 'domain/components/application/ApplicationOverview';
import DraftApplication from 'domain/types/DraftApplication';
import { useState } from 'react';
import {
  Button,
  FormCheck,
  Row,
  Col,
  FormGroup,
  FormLabel,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';

interface ReviewPageProps {
  data: DraftApplication;
  errors: string[];
  submitStatus: ActionStatus;
  onSubmit: () => Promise<void>;
  onReturnToDashboard: () => void;
  onResetClick: () => void;
  onBackToApplicationClick: () => void;
}

const ReviewPage: React.FC<ReviewPageProps> = ({
  errors,
  data,
  submitStatus,
  onBackToApplicationClick,
  onResetClick,
  onReturnToDashboard,
  onSubmit,
}) => {
  const [confirmSubmission, setConfirmSubmission] = useState(false);
  const [showAttest, setShowAttest] = useState(false);

  const ModalConfirmationAndAttestation = () => (
    <Modal
      show={showAttest}
      title={<h3>Confirmation and Attestation</h3>}
      body={
        <>
          <FormGroup as={Row} className='flex-nowrap m-5'>
            <Col>
              <FormCheck
                checked={confirmSubmission}
                type='checkbox'
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setConfirmSubmission(event.target.checked);
                }}
              />
            </Col>
            <FormLabel>
              By submitting this application, I affirm that I have legal
              authorization to access and use the unit for shared housing
              purposes. Furthermore, I attest that all application information
              is true, accurate, and complete, and I understand that any
              falsification, omission, or concealment of material fact may
              subject me to fines, citations, and/or revocation of my
              registration.
            </FormLabel>
          </FormGroup>
          <div className='d-flex justify-content-end'>
            <Button
              className='mr-auto'
              variant='link'
              onClick={() => {
                setConfirmSubmission(false);
                setShowAttest(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type='button'
              className=' mt-2 px-5'
              onClick={async () => {
                await onSubmit();
                setShowAttest(false);
              }}
              disabled={
                submitStatus === ActionStatus.saving || !confirmSubmission
              }
            >
              Submit
            </Button>
          </div>
        </>
      }
    />
  );

  return (
    <Page template={PageTemplates.fullWidth}>
      <div className='d-flex mb-5 '>
        <h2 className='d-inline-block mr-auto'>Review Application</h2>
        <Button className='px-5' variant='info' onClick={() => window.print()}>
          <FontAwesomeIcon icon={faPrint} />
        </Button>
      </div>
      {errors.length > 0 && (
        <ErrorAlert>
          <ul>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </ErrorAlert>
      )}
      <ApplicationOverview data={data} />
      <Button
        type='button'
        variant='link'
        className='float-left mt-3 p-0'
        onClick={onBackToApplicationClick}
      >
        Back to Application
      </Button>
      <div className='float-right'>
        <Button
          type='button'
          className=' mt-2 px-5'
          onClick={() => setShowAttest(true)}
        >
          Confirm
        </Button>
      </div>

      {ModalConfirmationAndAttestation()}

      <Modal
        show={submitStatus === ActionStatus.saved}
        title='Your application has been submitted.'
        body={
          <p>
            The Department of Business Affairs will review this application and
            will respond to the email address provided. If you would like to
            submit additional applications, be advised you will be required to
            get a{' '}
            <Link to='/profile#shuol'>
              {"Shared Housing Unit Operator's License"}
            </Link>{' '}
            first.
          </p>
        }
        footer={
          <>
            <div className='mr-auto'>
              <Button variant='primary' onClick={onResetClick}>
                Start Another Application
              </Button>
            </div>

            <Button variant='outline-primary' onClick={onReturnToDashboard}>
              Return to Dashboard
            </Button>
          </>
        }
      />
    </Page>
  );
};

export default ReviewPage;
